/**
 * @engine/vfx — VFX preset builders (effects group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts to
 * keep that module focused on content-type / importer / serialization.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Black Hole - Multi-system effect with accretion disk, relativistic jets, and Hawking radiation.
 *
 * Systems:
 * 1. Accretion Disk - Particles spiral inward with vortex motion, HDR orange/white colors
 * 2. Jet Up - Relativistic jet shooting upward, purple/blue HDR
 * 3. Jet Down - Relativistic jet shooting downward
 * 4. Event Horizon - Dark core particles, very dim
 * 5. Hawking Radiation - Rare bright escaping particles
 */

export function createForceFieldVFX(
  name: string = "Force Field",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "forcefield_effect",
    name: "Force Field",
    nodes: [
      // Spawn
      {
        id: "spawn",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 100 },
        contextId: "ctx_spawn",
      },
      // Initialize
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 3, max: 5 },
        contextId: "ctx_init",
      },
      {
        id: "init_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 80,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init",
      },
      {
        id: "init_noise",
        type: "vfx.init.noise_offset",
        position: { x: 50, y: 250 },
        inputValues: {
          amplitude: { x: 20, y: 20, z: 20 },
          frequency: 0.5,
          seed: 42,
        },
        contextId: "ctx_init",
      },
      {
        id: "init_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 350 },
        inputValues: { speed: 5, speedVariance: 3 },
        contextId: "ctx_init",
      },
      {
        id: "init_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 450 },
        inputValues: { min: 1, max: 3 },
        contextId: "ctx_init",
      },
      {
        id: "init_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 550 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 2, g: 4, b: 8, a: 1 } },
              { t: 0.5, color: { r: 4, g: 2, b: 8, a: 1 } },
              { t: 1, color: { r: 8, g: 4, b: 6, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init",
      },
      // Update
      {
        id: "update_conform",
        type: "vfx.update.conform_to_sphere",
        position: { x: 50, y: 50 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          radius: 60,
          strength: 10,
          mode: 0,
        },
        contextId: "ctx_update",
      },
      {
        id: "update_orbit",
        type: "vfx.update.orbit",
        position: { x: 50, y: 150 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          axis: { x: 0.2, y: 1, z: 0.1 },
          speed: 3,
          radius: 0,
          radiusVariance: 0,
        },
        contextId: "ctx_update",
      },
      {
        id: "update_limit",
        type: "vfx.update.limit_velocity",
        position: { x: 50, y: 250 },
        inputValues: { minSpeed: 2, maxSpeed: 20, damping: 0.5 },
        contextId: "ctx_update",
      },
      {
        id: "update_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 1 },
              { t: 0.9, v: 1 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update",
      },
      // Output
      {
        id: "output",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, softParticle: true },
        contextId: "ctx_output",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 600 },
        nodeIds: [
          "init_lifetime",
          "init_position",
          "init_noise",
          "init_velocity",
          "init_size",
          "init_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 850 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_conform",
          "update_orbit",
          "update_limit",
          "update_alpha",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1300 },
        size: { width: 280, height: 120 },
        nodeIds: ["output"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow1",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow2",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow3",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: { ctx_init: { capacity: 500, boundsMode: "auto" } },
    capacity: { maxParticles: 500, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Smoke - Realistic smoke using curl noise.
 * Demonstrates: curl_noise, noise_offset, color_over_life
 */
